// wayai unbind [<selector>] — narrow or clear this worktree's scope.
// (Also reachable as `wayai unlock`, the deprecated pre-rename alias.)
//
// Bare, it is the full reset: every axis, and the legacy per-axis files with
// them. With a selector it removes ONE entry, which is what makes a widened
// scope reversible without throwing the whole tripwire away.
//
// Failures are raised as `expected` errors, not caught here — see the note in
// `use.ts` for why a local catch-all would cost the Sentry report on a genuine
// crash.

import { expected } from '../lib/expected.js';
import { findAddFlag, resolveScopeSelector, resolveScopeTarget } from '../lib/scope-selector.js';
import {
  AXIS_ORDER,
  axisNoun,
  clearScope,
  describeScope,
  readScope,
  WHOLE_SCOPE,
  removeFromScope,
  type ScopeAxis,
} from '../lib/worktree-scope.js';

export async function unbindCommand(args: string[]): Promise<void> {
  // `--add` widens; there is no widening spelling of unbind. Silently ignoring it
  // would delete an entry the user believed they were adding. EVERY spelling is
  // refused here — bare and valued alike — and the recovery names this verb's own
  // options: a message written for `use` would answer a removal request with two
  // commands that move the scope the other way.
  const addFlag = findAddFlag(args);
  if (addFlag !== undefined) {
    throw expected(
      [
        `\`wayai unbind\` does not take ${JSON.stringify(addFlag)}.`,
        '  wayai unbind <selector>      # remove one entry from the worktree scope',
        `  wayai unbind                 # clear the whole scope (${WHOLE_SCOPE})`,
        '  wayai use --add <selector>   # WIDEN the scope instead (the opposite change)',
      ].join('\n'),
    );
  }

  const selector = resolveScopeSelector('unbind', args);

  if (!selector) {
    // The full reset is gated on the user having typed NOTHING — not on "no
    // selector could be resolved". Those differ, and the gap is destructive:
    // `parseRoutingTokens` records no selector for a value-taking flag whose value is missing
    // (`wayai unbind --base`) or which this verb does not honour
    // (`wayai unbind --org acme`), so an invocation that plainly named something
    // arrived here indistinguishable from a bare one and cleared EVERY axis plus
    // the legacy files, reporting it as deliberate with exit 0.
    //
    // Gating on emptiness closes the whole class at once, including spellings
    // neither this module nor `parseRoutingTokens` knows about yet. The specific
    // "flag with no value" refusal in `scope-selector.ts` gives the common case a
    // better message; this is the safety net behind it.
    if (args.length > 0) {
      throw expected(
        [
          `\`wayai unbind ${args.join(' ')}\` does not name anything to remove.`,
          '',
          'Refusing rather than clearing the whole scope — that is what a BARE',
          '`wayai unbind` does, and it is not what this invocation asked for.',
          '  wayai unbind <selector>   # remove one entry',
          `  wayai unbind              # clear the whole scope (${WHOLE_SCOPE})`,
        ].join('\n'),
      );
    }
    // Reported from the CONTENTS, not from whether a file was unlinked: removing
    // the last entry leaves an empty `hubs: []\nbases: []` document behind, and
    // going by the unlink would then announce a clearing of nothing.
    const described = describeScope(readScope());
    clearScope();
    console.log(
      described ? `Worktree scope cleared (was ${described}).` : 'No worktree scope to clear.',
    );
    return;
  }

  // A scope entry outlives the folder it came from, so a literal match is tried
  // FIRST: after `rm -rf wayai-ws/bases/crm`, `wayai unbind bases/crm` must still
  // be able to drop `crm` — otherwise a stale entry could only be removed by
  // clearing the whole scope. Every axis is checked, not just the one the
  // selector routed to: a bare base slug whose folder is gone routes to `hubs`
  // (nothing on disk says otherwise), and resolving it there would answer with a
  // hub miss about an id plainly sitting in the scope.
  const scope = readScope();
  const literal = AXIS_ORDER.filter((axis) => scope[axis].includes(selector.target));
  const literalAxis = literal.includes(selector.axis)
    ? selector.axis
    : literal.length === 1
      ? literal[0]
      : null;
  if (literalAxis) {
    announce(literalAxis, selector.target, removeFromScope(literalAxis, selector.target) ?? []);
    return;
  }

  const { axis, id } = resolveScopeTarget(selector);
  const remaining = removeFromScope(axis, id);
  if (!remaining) {
    const described = describeScope(scope);
    throw expected(
      [
        `${axisNoun(axis)} ${id} is not in this worktree's scope.`,
        described
          ? `This worktree is scoped to ${described}.`
          : 'This worktree has no scope.',
      ].join('\n'),
    );
  }
  announce(axis, id, remaining);
}

function announce(axis: ScopeAxis, id: string, remaining: string[]): void {
  const noun = axisNoun(axis);
  console.log(
    remaining.length > 0
      ? `Removed ${noun} ${id} from the worktree scope (${axis}: ${remaining.join(', ')}).`
      : `Removed ${noun} ${id} — this worktree is no longer scoped to any ${noun}.`,
  );
}
